import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FaPlus, FaTrash, FaTimes } from "react-icons/fa";
import { useAdmin } from "../context/AdminContext";
import Alert from "../components/Alert";

const AdminAnnouncements = () => {
  const {
    announcements,
    announcementsLoading,
    fetchAnnouncements,
    createAnnouncement,
    deleteAnnouncement,
    alert,
    showAlert,
  } = useAdmin();

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // ✅ confirm delete modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchAnnouncements?.();
  }, []);

  const rows = useMemo(
    () => (Array.isArray(announcements) ? announcements : []),
    [announcements]
  );

  const resetModal = () => {
    setTitle("");
    setDescription("");
  };

  const closeModal = () => {
    setOpen(false);
    resetModal();
  };

  const openConfirm = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (deleteLoading) return;
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const t = title.trim();
    const d = description.trim();

    if (!t || !d) {
      showAlert("error", "Title & Description required");
      return;
    }

    const ok = await createAnnouncement({ title: t, description: d });
    if (ok) closeModal();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      const ok = await deleteAnnouncement(deleteId);
      if (!ok) showAlert("error", "Delete failed");
      closeConfirm();
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Alert
        type={alert.type}
        message={alert.message}
        isOpen={alert.isOpen}
        onClose={() => showAlert({ isOpen: false, type: "", message: "" })}
      />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto pt-8 lg:pt-12"
      >
        {/* Main Card - DARK BLUE Background with Yellow Border */}
        <div className="bg-[#0B192C] border border-yellow-400/20 rounded-[2rem] p-6 lg:p-8 shadow-2xl">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
                Announcements
              </h1>
              <p className="text-slate-300 font-medium mt-2">
                Create / manage announcements shown on user side.
              </p>
            </div>

            <button
              onClick={() => setOpen(true)}
              className="h-12 px-6 rounded-2xl bg-yellow-400 text-[#0B192C] font-black
                       shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 hover:shadow-yellow-400/40 
                       active:scale-95 transition-all flex items-center gap-2"
            >
              <FaPlus />
              Add Announcement
            </button>
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-yellow-400/20">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#132A45]">
                  <tr className="text-left text-yellow-400 font-bold uppercase tracking-wider text-[11px]">
                    <th className="p-4 w-[240px]">Title</th>
                    <th className="p-4">Description</th>
                    <th className="p-4 w-[160px]">Created</th>
                    <th className="p-4 w-[100px] text-right">Action</th>
                  </tr>
                </thead>

                <tbody className="bg-[#0B192C]">
                  {announcementsLoading ? (
                    <tr>
                      <td className="p-6 text-slate-400 text-center font-medium" colSpan={4}>
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td className="p-6 text-slate-400 text-center font-medium" colSpan={4}>
                        No announcements yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((a) => (
                      <tr
                        key={a._id}
                        className="border-t border-yellow-400/10 text-white hover:bg-[#132A45] transition-colors"
                      >
                        <td className="p-4 font-bold text-white">{a.title}</td>
                        <td className="p-4 text-slate-300 font-medium">
                          <div className="line-clamp-2">{a.description}</div>
                        </td>
                        <td className="p-4 text-slate-400 font-medium text-xs">
                          {a.createdAt ? new Date(a.createdAt).toLocaleString() : "-"}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => openConfirm(a._id)}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-xl
                                     bg-rose-500/10 border border-rose-500/20 text-rose-500 hover:bg-rose-500 hover:text-white
                                     transition-all active:scale-95"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        {open && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-[#0B192C]/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="w-full max-w-xl rounded-[2rem] bg-[#0B192C] border border-yellow-400/30 shadow-2xl"
            >
              <div className="flex items-center justify-between p-6 border-b border-yellow-400/20">
                <h2 className="text-xl font-black text-white">Add Announcement</h2>
                <button
                  onClick={closeModal}
                  className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10
                           flex items-center justify-center transition-colors text-white"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={onSubmit} className="p-6 space-y-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="mt-2 w-full h-12 rounded-xl bg-[#132A45] border border-yellow-400/20
                             text-white px-4 font-medium outline-none focus:border-yellow-400 transition-all"
                    placeholder="Announcement title"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    className="mt-2 w-full rounded-xl bg-[#132A45] border border-yellow-400/20
                             text-white p-4 font-medium outline-none focus:border-yellow-400 transition-all resize-none"
                    placeholder="Write announcement details..."
                  />
                </div>

                <div className="flex gap-3 justify-end pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="h-12 px-6 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10
                             text-white font-bold transition-all"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="h-12 px-8 rounded-xl bg-yellow-400 text-[#0B192C] font-black 
                             shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 active:scale-95 transition-all"
                  >
                    Save
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* ✅ Confirm Delete Modal */}
        {confirmOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-[#0B192C]/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="w-full max-w-md rounded-[2rem] bg-[#0B192C] border border-rose-500/30 shadow-2xl"
            >
              <div className="p-6 border-b border-rose-500/20">
                <h3 className="text-xl font-black text-white">Confirm Delete</h3>
                <p className="text-sm font-medium text-slate-300 mt-2">
                  Are you sure you want to delete this announcement? This action can’t be undone.
                </p>
              </div>

              <div className="p-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeConfirm}
                  disabled={deleteLoading}
                  className="h-11 px-5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10
                           text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                  className="h-11 px-6 rounded-xl bg-rose-600 text-white font-black 
                           shadow-lg shadow-rose-600/30 hover:bg-rose-500 active:scale-95 transition-all
                           disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? "Deleting..." : "Yes, Delete"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </>
  );
};

export default AdminAnnouncements;